import React, { useState, useEffect } from 'react';
import { Container } from "react-bootstrap";
import { useData } from '../../context/DataContext';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Tooltip } from '@mui/material';
import AddCategoriesModel from './AddCategoriesModel';
import ModeEditOutlineSharpIcon from '@mui/icons-material/ModeEditOutlineSharp';
import EditCategoriesModel from './EditCategoriesModel';
import MaterialReactTableWithSearch from '../MaterialTable/MaterialReactTableWithSearch';
import Loading from '../Loading/Loading';
import { fetchFromApi } from '../../config/api';
import API_ENDPOINTS from '../../config/apiConfig';

function Categories() {
    const { categoriesData, loading, error, fetchCategoriesData } = useData();
    const [modalShow, setModalShow] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [editData, setEditData] = useState({});
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    useEffect(() => {
        if (!categoriesData) fetchCategoriesData();
    }, [fetchCategoriesData, categoriesData]);

    if (loading) return <Loading />;
    if (error) return <div style={{ padding: 24, color: 'red' }}>Error: {error}</div>;
    if (!categoriesData) return <div style={{ padding: 24 }}>No categories data available.</div>;

    const formatDate = (dateString) => {
        const d = new Date(dateString);
        return isNaN(d.getTime()) ? "-" : d.toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }).replace(',', '');
    };

    const activeEditModal = (row) => { setEditData(row.original); setEditModal(true); };

    const deleteRow = async (row) => {
        if (window.confirm("Are you sure you want to delete this category?")) {
            setDeleteLoading(true);
            try {
                const data = await fetchFromApi(API_ENDPOINTS.CATEGORIES_DELETING, 'POST', { id: row.original.id });
                if (data.status === "success") await fetchCategoriesData();
            } catch (err) {
                setDeleteError("Failed to delete category. Please try again.");
            } finally {
                setDeleteLoading(false);
            }
        }
    };

    const renderRowActions = ({ row }) => (
        <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            <Tooltip title="Edit">
                <IconButton size="small" onClick={() => activeEditModal(row)}>
                    <ModeEditOutlineSharpIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton size="small" onClick={() => deleteRow(row)} disabled={deleteLoading}>
                    <DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} />
                </IconButton>
            </Tooltip>
        </Box>
    );

    const columns = [
        { accessorFn: (row, index) => index + 1, header: 'SNO', id: 'id', size: 60 },
        { accessorKey: 'category_name', header: 'Category Name' },
        { accessorKey: 'inserted_time', header: 'Created Date', Cell: ({ cell }) => <span>{formatDate(cell.getValue())}</span> },
        { accessorKey: 'updated_time', header: 'Updated Date', Cell: ({ cell }) => <span>{formatDate(cell.getValue())}</span> },
        {
            id: 'actions', header: 'Actions', Cell: renderRowActions, size: 100,
            muiTableBodyCellProps: { sx: { position: 'sticky', right: 0, backgroundColor: '#fff', zIndex: 1 } },
            muiTableHeadCellProps: { sx: { position: 'sticky', right: 0, backgroundColor: '#fff', zIndex: 1 } },
        }
    ];

    return (
        <Container fluid className="px-0">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Categories</h1>
                    <p className="page-subtitle">{categoriesData.length} categories total</p>
                </div>
                <button className="btn-primary-orange" onClick={() => setModalShow(true)}>
                    <i className="fa-solid fa-plus"></i> Add Category
                </button>
            </div>

            <div className="table-container">
                <MaterialReactTableWithSearch columns={columns} data={categoriesData} />
            </div>

            {deleteError && <p style={{ color: '#ef4444', marginTop: 12, fontSize: '0.875rem' }}>{deleteError}</p>}

            {modalShow && <AddCategoriesModel show={modalShow} onHide={() => setModalShow(false)} />}
            {editModal && (
                <EditCategoriesModel
                    show={editModal}
                    editData={editData}
                    onHide={() => { setEditModal(false); setEditData({}); }}
                />
            )}
        </Container>
    );
}

export default Categories;
